import type {Content} from '@prismicio/client'
import {isFilled} from '@prismicio/client'
import {PrismicNextImage} from '@prismicio/next/pages'
import type {SliceComponentProps} from '@prismicio/react'
import {PrismicRichText} from '@prismicio/react'
import dynamic from 'next/dynamic'
import type {AnimatedTextBlockSliceDefaultPrimaryLeftAssetsItem} from 'prismicio-types'
import type {FC} from 'react'
import {useState} from 'react'
import {Icon} from 'src/common/Icon'
import {ModalVideoPlayer} from 'src/common/ModalVideoPlayer'
import {EVENTS, track} from 'src/common/helpers'
import type {Context} from 'src/common/types'
import {ContentSection} from './components/ContentSection'
import {GridSeparators} from './components/GridSeparators'
import {ParallaxWrapper} from './components/ParallaxWrapper'
import type {MobileSlots} from './components/ScrollableSection'

const VideoPlayer = dynamic(() => import('src/common/VideoPlayer').then((module) => module.VideoPlayer), {
  ssr: false,
})

/**
 * Props for `AnimatedTextBlock`.
 */
export type AnimatedTextBlockProps = SliceComponentProps<Content.AnimatedTextBlockSlice> & {
  context: Context
}

const GRID_CLASSES =
  'relative grid grid-cols-[20px_auto_20px] grid-rows-[40px_auto_40px] md:grid-cols-[40px_auto_640px_auto_40px] md:grid-rows-[60px_auto_60px] lg:grid-cols-[20px_auto_20px] lg:grid-rows-[80px_auto_80px] xl:grid-cols-[auto_1200px_auto]'

/**
 * Component for "AnimatedTextBlock" Slices.
 */
const AnimatedTextBlock: FC<AnimatedTextBlockProps> = ({slice, index: componentIndex, context}) => {
  const leftAssets = isFilled.group(slice.primary.left_assets) ? slice.primary.left_assets : []
  const rightAssets = isFilled.group(slice.primary.right_assets) ? slice.primary.right_assets : []
  const assetsArr = [...leftAssets, ...rightAssets]
  const [activeVideo, setActiveVideo] = useState<number | null>(null)

  const handleClick = (index: number) => {
    track({
      event: EVENTS.CLICK,
      mktgId: `animated-text-block-video-button`,
      location: `testimonial-video-${componentIndex}`,
      pageName: context.pageUid,
      dataType: 'video-button',
    })

    setActiveVideo(index)
  }

  let mobileSlots: MobileSlots | undefined

  if (assetsArr.length > 0) {
    mobileSlots = (assetsArr as AnimatedTextBlockSliceDefaultPrimaryLeftAssetsItem[]).reduce((acc, asset) => {
      if (!asset.slot_name) {
        return acc
      }

      acc[asset.slot_name] = {
        src: (
          <PrismicNextImage
            field={asset.image}
            sizes="(max-width:1024px) 90vw, 600px"
            alt=""
            className="max-w-[185px]"
          />
        ),
        speed: 0.22,
        marginBottom: 48,
      }

      return acc
    }, {} as MobileSlots)
  }

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="w-full bg-green-dark pt-20 md:pt-[98px]"
      data-nav-dark
    >
      <div className={GRID_CLASSES}>
        <div className="absolute inset-0 z-10 h-10 bg-gradient-to-b from-green-dark to-transparent md:h-5" />
        <div className="absolute bottom-0 left-0 z-10 h-10 w-full bg-gradient-to-t from-green-dark to-transparent md:h-5" />
        <GridSeparators />
        <div className="ml-[-1px] mt-[-1px] grid grid-cols-1 rounded-[4px] border border-green-light md:max-lg:col-start-3 md:max-lg:row-start-2  lg:grid-cols-[280px_auto_280px] lg:py-16">
          <div className="col-start-1 flex flex-col items-center gap-[40%] pt-[210px] max-lg:hidden">
            {slice.primary.left_assets.map((asset, index) => (
              <div
                key={index}
                className="w-[60%] animate-fadeIn opacity-0"
                style={{
                  animationDelay: '300ms',
                }}
              >
                <ParallaxWrapper speed={[1.8, 2.2][index % 2]} className="w-full">
                  <PrismicNextImage field={asset.image} sizes="(min-width:1024px) 20vw, 60vw" alt="" />
                </ParallaxWrapper>
              </div>
            ))}
          </div>

          <div className="col-span-1 flex flex-col gap-16 max-lg:p-6">
            <ContentSection
              title={slice.primary.title}
              textBlock={slice.primary.static_text_block}
              mobileSlots={mobileSlots}
              scrollableBlock={slice.primary.scroll_text_block}
            />
            <div className="flex flex-col gap-6 lg:gap-8">
              <PrismicRichText
                field={slice.primary.video_section[0]?.title}
                components={{
                  paragraph: ({children}) => (
                    <span className="text-button-s flex items-center text-moss-white">{children}</span>
                  ),
                  em: () => <div className="mx-2 h-[5px] w-[5px] rounded-[1px] bg-moss-white/[70%]" />,
                  strong: ({children}) => <span className="text-moss-white/[70%]">{children}</span>,
                }}
              />
              <div className="relative w-fit">
                {isFilled.link(slice.primary.video_section[0]?.preview_video) && (
                  <VideoPlayer
                    src={slice.primary.video_section[0]?.preview_video.url}
                    className="!mx-0 w-[282px] max-w-[382px] !rounded-[4px] lg:w-[380px] lg:max-w-[380px]"
                    autoplay
                    loop
                    aspectRatio="aspect-auto"
                    hideControls
                    startMuted
                  />
                )}
                <button
                  className="text-button-s pointer-events-auto absolute left-1/2 top-1/2 flex h-12 w-fit translate-x-[-50%] translate-y-[-50%] items-center gap-2.5 rounded-[4px] bg-moss-white/[30%] p-4 text-moss-white backdrop-blur-[6px] hover:brightness-110"
                  onClick={() => handleClick(1)}
                >
                  <Icon name="play" className="size-3 text-moss-white" />
                  {slice.primary.video_section[0]?.button_label}
                </button>
              </div>
            </div>
          </div>

          <div className="col-start-3 flex flex-col items-center pt-[450px] max-lg:hidden">
            {slice.primary.right_assets.map((asset, index) => (
              <ParallaxWrapper key={index} speed={1.4} className="w-[60%]">
                <PrismicNextImage field={asset.image} sizes="(min-width:1024px) 20vw, 60vw" alt="" />
              </ParallaxWrapper>
            ))}
          </div>
        </div>
      </div>
      {activeVideo === null ? null : (
        <ModalVideoPlayer
          className="h-auto lg:h-[66.25vh]"
          onClose={() => setActiveVideo(null)}
          src={slice.primary.video_section[0]?.video_link.embed_url}
          autoplay
        />
      )}
    </section>
  )
}

export default AnimatedTextBlock
